import socketService from "@/socketService"

const initialState = () => ({
  snapshot: null,
  activeSessionId: null,
  activeCode: null,
  mode: null,
  connected: false,
  listenersReady: false,
  joining: false,
  actionPending: false,
  answerPending: false,
  selectedAnswerIds: [],
  answerLocked: false,
  error: null,
})

const state = initialState()
let listenerCallbacks = null

const mutations = {
  SET_CONNECTED(state, connected) {
    state.connected = connected
  },
  SET_LISTENERS_READY(state, ready) {
    state.listenersReady = ready
  },
  SET_JOINING(state, joining) {
    state.joining = joining
  },
  SET_ACTION_PENDING(state, pending) {
    state.actionPending = pending
  },
  SET_ANSWER_PENDING(state, pending) {
    state.answerPending = pending
  },
  SET_ACTIVE_SESSION(state, { sessionId, code, mode }) {
    state.activeSessionId = sessionId
    state.activeCode = code || null
    state.mode = mode
  },
  SET_SNAPSHOT(state, snapshot) {
    if (!snapshot) return
    if (
      state.snapshot &&
      String(state.snapshot._id) === String(snapshot._id) &&
      Number(snapshot.stateVersion) < Number(state.snapshot.stateVersion)
    ) {
      return
    }
    const previousQuestionId = state.snapshot && state.snapshot.question ? String(state.snapshot.question._id) : null
    const nextQuestionId = snapshot.question ? String(snapshot.question._id) : null
    if (previousQuestionId !== nextQuestionId) {
      state.selectedAnswerIds = []
      state.answerLocked = false
    }
    if (snapshot.personal && snapshot.personal.answered) {
      const selectedIds = Array.isArray(snapshot.personal.selectedAnswerIds)
        ? snapshot.personal.selectedAnswerIds
        : [snapshot.personal.selectedAnswerId]
      state.selectedAnswerIds = selectedIds.filter(Boolean).map(String)
      state.answerLocked = true
    }
    state.snapshot = snapshot
    state.activeSessionId = String(snapshot._id)
  },
  SET_SELECTED_ANSWERS(state, answerIds) {
    state.selectedAnswerIds = Array.from(new Set((answerIds || []).filter(Boolean).map(String)))
  },
  SET_ANSWER_LOCKED(state, locked) {
    state.answerLocked = Boolean(locked)
  },
  SET_ERROR(state, error) {
    state.error = error || null
  },
  RESET_STATE(state) {
    Object.assign(state, initialState(), {
      connected: Boolean(socketService.socket && socketService.socket.connected),
      listenersReady: Boolean(listenerCallbacks),
    })
  },
}

const actions = {
  registerListeners({ state, commit, dispatch }) {
    if (state.listenersReady && listenerCallbacks) return

    listenerCallbacks = {
      state: (snapshot) => commit("SET_SNAPSHOT", snapshot),
      error: (payload) => commit("SET_ERROR", (payload && payload.message) || "Beim Live-Quiz ist ein Fehler aufgetreten."),
      connect: () => {
        commit("SET_CONNECTED", true)
        if (state.activeSessionId && state.mode && (!state.snapshot || !["finished", "cancelled", "expired"].includes(state.snapshot.state))) {
          dispatch("rejoin")
        }
      },
      disconnect: () => commit("SET_CONNECTED", false),
    }

    socketService.on("liveQuiz:state", listenerCallbacks.state)
    socketService.on("liveQuiz:error", listenerCallbacks.error)
    socketService.on("connect", listenerCallbacks.connect)
    socketService.on("disconnect", listenerCallbacks.disconnect)
    commit("SET_CONNECTED", Boolean(socketService.socket && socketService.socket.connected))
    commit("SET_LISTENERS_READY", true)
  },

  async join({ commit, dispatch }, { sessionId = null, code = null, mode = "player" }) {
    dispatch("registerListeners")
    commit("SET_JOINING", true)
    commit("SET_ERROR", null)
    try {
      const response = await socketService.emitAck("liveQuiz:join", { sessionId, code, mode }, 10000)
      const snapshot = response.snapshot
      commit("SET_ACTIVE_SESSION", {
        sessionId: snapshot ? String(snapshot._id) : sessionId,
        code: snapshot ? snapshot.joinCode : code,
        mode,
      })
      if (snapshot) commit("SET_SNAPSHOT", snapshot)
      return snapshot
    } catch (error) {
      commit("SET_ERROR", error.message)
      throw error
    } finally {
      commit("SET_JOINING", false)
    }
  },

  async rejoin({ state, commit }) {
    if (!state.activeSessionId || !state.mode || state.joining) return null
    commit("SET_JOINING", true)
    try {
      const response = await socketService.emitAck(
        "liveQuiz:join",
        { sessionId: state.activeSessionId, code: state.activeCode, mode: state.mode },
        10000,
      )
      if (response.snapshot) commit("SET_SNAPSHOT", response.snapshot)
      commit("SET_ERROR", null)
      return response.snapshot
    } catch (error) {
      commit("SET_ERROR", error.message)
      return null
    } finally {
      commit("SET_JOINING", false)
    }
  },

  async leave({ state, commit }, options = {}) {
    const sessionId = state.activeSessionId
    if (sessionId && socketService.socket && socketService.socket.connected) {
      try {
        await socketService.emitAck("liveQuiz:leave", { sessionId, intentional: Boolean(options.intentional) }, 5000)
      } catch (error) {
        console.warn("Live-Quiz konnte nicht sauber verlassen werden:", error.message)
      }
    }
    commit("RESET_STATE")
  },

  async start({ state, commit }) {
    if (!state.snapshot) return
    commit("SET_ACTION_PENDING", true)
    commit("SET_ERROR", null)
    try {
      await socketService.emitAck("liveQuiz:host:start", {
        sessionId: state.snapshot._id,
        stateVersion: state.snapshot.stateVersion,
      })
    } catch (error) {
      commit("SET_ERROR", error.message)
      throw error
    } finally {
      commit("SET_ACTION_PENDING", false)
    }
  },

  async next({ state, commit }) {
    if (!state.snapshot) return
    commit("SET_ACTION_PENDING", true)
    commit("SET_ERROR", null)
    try {
      await socketService.emitAck("liveQuiz:host:next", {
        sessionId: state.snapshot._id,
        stateVersion: state.snapshot.stateVersion,
      })
    } catch (error) {
      commit("SET_ERROR", error.message)
      throw error
    } finally {
      commit("SET_ACTION_PENDING", false)
    }
  },

  async pause({ state, commit }) {
    if (!state.snapshot) return
    commit("SET_ACTION_PENDING", true)
    commit("SET_ERROR", null)
    try {
      await socketService.emitAck("liveQuiz:host:pause", {
        sessionId: state.snapshot._id,
        stateVersion: state.snapshot.stateVersion,
      })
    } catch (error) {
      commit("SET_ERROR", error.message)
      throw error
    } finally {
      commit("SET_ACTION_PENDING", false)
    }
  },

  async resume({ state, commit }) {
    if (!state.snapshot) return
    commit("SET_ACTION_PENDING", true)
    commit("SET_ERROR", null)
    try {
      await socketService.emitAck("liveQuiz:host:resume", {
        sessionId: state.snapshot._id,
        stateVersion: state.snapshot.stateVersion,
      })
    } catch (error) {
      commit("SET_ERROR", error.message)
      throw error
    } finally {
      commit("SET_ACTION_PENDING", false)
    }
  },

  async finish({ state, commit }, cancelled = false) {
    if (!state.snapshot) return
    commit("SET_ACTION_PENDING", true)
    commit("SET_ERROR", null)
    try {
      await socketService.emitAck(cancelled ? "liveQuiz:host:cancel" : "liveQuiz:host:finish", {
        sessionId: state.snapshot._id,
        stateVersion: state.snapshot.stateVersion,
      })
    } catch (error) {
      commit("SET_ERROR", error.message)
      throw error
    } finally {
      commit("SET_ACTION_PENDING", false)
    }
  },

  toggleAnswer({ state, commit }, answerId) {
    if (state.answerLocked || state.answerPending) return
    const normalizedId = String(answerId)
    const selected = state.selectedAnswerIds.includes(normalizedId)
      ? state.selectedAnswerIds.filter((entry) => entry !== normalizedId)
      : [...state.selectedAnswerIds, normalizedId]
    commit("SET_SELECTED_ANSWERS", selected)
  },

  async answer({ state, commit }, answerPayload) {
    if (!state.snapshot || !state.snapshot.question || state.answerPending || state.answerLocked) return
    const sessionId = state.snapshot._id
    const questionId = state.snapshot.question._id
    const questionType = state.snapshot.question.type || "single"
    const payload = answerPayload && typeof answerPayload === "object" ? answerPayload : { answerIds: [answerPayload] }
    const answerIds = Array.isArray(payload.answerIds) ? payload.answerIds.filter(Boolean).map(String) : []
    if (questionType !== "estimate" && !answerIds.length) return
    if (answerIds.length) commit("SET_SELECTED_ANSWERS", answerIds)
    commit("SET_ANSWER_LOCKED", true)
    commit("SET_ANSWER_PENDING", true)
    commit("SET_ERROR", null)
    try {
      await socketService.emitAck(
        "liveQuiz:answer",
        {
          sessionId,
          questionId,
          answerId: answerIds.length === 1 ? answerIds[0] : null,
          answerIds,
          estimatedValue: payload.estimatedValue,
        },
        7000,
      )
    } catch (error) {
      commit("SET_ANSWER_LOCKED", false)
      if (questionType === "single") commit("SET_SELECTED_ANSWERS", [])
      commit("SET_ERROR", error.message)
      throw error
    } finally {
      commit("SET_ANSWER_PENDING", false)
    }
  },

  clearError({ commit }) {
    commit("SET_ERROR", null)
  },

  reset({ commit }) {
    if (listenerCallbacks) {
      socketService.off("liveQuiz:state", listenerCallbacks.state)
      socketService.off("liveQuiz:error", listenerCallbacks.error)
      socketService.off("connect", listenerCallbacks.connect)
      socketService.off("disconnect", listenerCallbacks.disconnect)
      listenerCallbacks = null
    }
    commit("RESET_STATE")
  },
}

const getters = {
  snapshot: (state) => state.snapshot,
  isConnected: (state) => state.connected,
  isBusy: (state) => state.joining || state.actionPending,
  selectedAnswerIds: (state) => state.selectedAnswerIds,
}

export const liveQuizStore = {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
}
